import React, { Fragment, useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

import apiFetch from '@wordpress/api-fetch';
import { __, _n, sprintf } from '@wordpress/i18n';
import DOMPurify from 'dompurify';

import IconCaret from 'images/icon-caret-gray.svg';
import IconReset from 'images/icon-reset.svg';
import IconSearch from 'images/icon-search.svg';

import makeHierarchicalTaxonomyMap from './makeHierarchicalTaxonomyMap';
import stripTags from '../../js/utils/stripTags';

import './longformList.scss';

/**
 * Format flat array of term IDs into parent/child pairs from given mapping.
 * @param {array} terms - array of term IDs
 * @param {object} taxMap - hierarchical taxonomy map
 * @returns {object}
 */
const prepareTermsForDisplay = (terms, taxMap) => {
  const display = {};
  // First pass, get parent terms.
  terms.forEach((term) => {
    const tStr = (term).toString();
    if (Object.keys(taxMap).includes(tStr)) {
      display[taxMap[tStr].name] = '';
    }
  });

  // Second pass, children.
  terms.forEach((term) => {
    const tStr = (term).toString();
    Object.keys(taxMap).forEach((parent) => {
      if (Object.keys(taxMap[parent].children).includes(tStr)) {
        display[taxMap[parent].name] = taxMap[parent].children[tStr].name;
      }
    });
  });
  return display;
};

const PER_PAGE = 10;

const LongformList = ({
  parent,
  postType,
  excludedFilters,
  hideTextSearch,
}) => {
  const [items, setItems] = useState([]);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [longformTaxonomy, setLongformTaxonomy] = useState({});
  const [filters, setFilters] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [searchString, setSearchString] = useState('');
  const [searchIndex, setSearchIndex] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isToolbarOpen, setIsToolbarOpen] = useState(false);

  const searchInput = useRef();

  const basePath = `/wp-json/wp/v2/${postType}`;
  const searchParams = new URLSearchParams({
    parent,
    per_page: PER_PAGE,
  });

  useEffect(() => {
    const fetchItems = () => {
      const collected = [];
      let totalChunks;

      apiFetch({
        path: `${basePath}?${searchParams.toString()}`,
        parse: false,
      })
        .then((response) => {
          totalChunks = parseInt(response.headers.get('X-WP-TotalPages'), 10);
          return response.json();
        })
        .then(async (data) => {
          // Store first chunk.
          collected.push(...data);

          // Fetch the rest.
          const values = await Promise
            .allSettled([...Array(totalChunks - 1).keys()]
              .map((chunk) => {
                searchParams.set('page', chunk + 2);
                return apiFetch({
                  path: `${basePath}?${searchParams.toString()}`,
                });
              }));
          values.forEach((moreData) => collected.push(...moreData.value));

          setItems(collected);
          setSearchIndex(collected.map((item) => ({
            id: item.id,
            textContent:
              stripTags(`${item.title.rendered} ${item.content.rendered}`)
                .toLowerCase(),
          })));
        });
    };

    const fetchTaxonomy = () => {
      apiFetch({
        path: '/wp-json/wp/v2/longform-categorizations?per_page=99',
      })
        .then((data) => {
          setLongformTaxonomy(makeHierarchicalTaxonomyMap(data));
        });
    };

    if (parent) {
      fetchItems();
      fetchTaxonomy();
    }
  }, []);

  useEffect(() => {
    if (0 === items.length ||
        0 === Object.keys(longformTaxonomy).length) {
      return;
    }

    const parentTerms = Object.keys(longformTaxonomy).map(Number);
    const activeTerms = new Set();
    items.forEach((item) => {
      item['longform-categorizations']
        .filter((el) => parentTerms.includes(el))
        .filter((el) => ! excludedFilters.includes(el))
        .map((parentTerm) => activeTerms.add(parentTerm));
    });
    setFilters(Array.from(activeTerms));

    // Keys are parent term id, value is initialized to 0.
    // It will hold values of selected child terms.
    const initializedFilterVals = {};
    // eslint-disable-next-line no-return-assign
    activeTerms.forEach((term) => initializedFilterVals[term] = 0);
    setFilterValues(initializedFilterVals);
  }, [items, longformTaxonomy]);

  useEffect(() => {
    if (0 === items.length) {
      return;
    }

    let filtered = [...items];

    if (searchString) {
      const keywordHits = searchIndex
        .filter((idx) => idx.textContent.includes(searchString))
        .map((idx) => idx.id);
      filtered = [...filtered.filter((item) => keywordHits.includes(item.id))];
    }

    const filterTerms = Object
      .values(filterValues)
      .filter((term) => 0 < term);

    if (filterTerms.length) {
      // Item must contain all selected filter values.
      filtered = [...filtered.filter((item) => (
        filterTerms
          .filter((ft) => item['longform-categorizations'].includes(ft))
          .length === filterTerms.length
      ))];
    }

    setDisplayedItems(filtered);
  }, [filterValues, searchString, items]);

  useEffect(() => {
    setTotalPages(Math.ceil(displayedItems.length / PER_PAGE));
  }, [displayedItems]);

  if (
    0 === items.length
    || 0 === Object.keys(longformTaxonomy).length
  ) {
    return false;
  }

  return (
    <div className="longform-list-wrapper">
      <div
        className="filters"
        aria-controls="longform-list-status"
      >
        <button
          className="filter-toggle"
          type="button"
          aria-expanded={isToolbarOpen}
          aria-controls="filter-toogle-container"
          onClick={() => setIsToolbarOpen(! isToolbarOpen)}
        >
          {/* translators: %s show or hide */}
          {__(sprintf('%s Filters', isToolbarOpen ? 'Hide' : 'Show'), 'kauffman') /* eslint-disable-line max-len */}
          <img src={IconCaret} alt="" />
        </button>
        <div
          id="filter-toggle-container"
          className={
            `filters-wrapper ${isToolbarOpen ? 'expanded' : 'collapsed'}`
          }
          role="toolbar"
          aria-label={__('Filter Toolbar', 'kauffman')}
        >
          {! hideTextSearch
            ? (
              <div className="input-group">
                { /* eslint-disable-next-line jsx-a11y/label-has-for */ }
                <label
                  className="focus-only"
                  htmlFor="longform-text-search"
                >
                  {__('Search', 'kauffman')}
                </label>
                <input
                  className="keywords"
                  id="longform-text-search"
                  name="text-search"
                  type="text"
                  ref={searchInput}
                  placeholder={__('Search by topic or keyword', 'kauffman')}
                  onKeyDown={(evt) => {
                    if ('Enter' === evt.key) {
                      setSearchString(searchInput.current.value.toLowerCase());
                    }
                  }}
                />
                <button
                  type="button"
                  className="search-submit"
                  onClick={
                    // eslint-disable-next-line max-len
                    () => setSearchString(searchInput.current.value.toLowerCase())
                  }
                >
                  <span className="screen-reader-text">
                    {__('Search', 'kauffman')}
                  </span>
                  <img src={IconSearch} alt="" />
                </button>
              </div>
            )
            : null}
          {filters.map((termId) => (
            <Fragment key={termId}>
              { /* eslint-disable-next-line jsx-a11y/label-has-for */ }
              <label
                htmlFor={`tax-filter-${termId}`}
                className="screen-reader-only"
              >
                {longformTaxonomy[termId].name}
              </label>
              <div className="Dropdown-root">
                <select
                  id={`tax-filter-${termId}`}
                  className="Dropdown-control"
                  name={longformTaxonomy[termId].name}
                  onChange={(evt) => setFilterValues({
                    ...filterValues,
                    [termId]: parseInt(evt.target.value, 10),
                  })}
                  value={filterValues[termId]}
                >
                  <option
                    key={0}
                    value={0}
                  >
                    {longformTaxonomy[termId].name}
                  </option>
                  {Object.keys(longformTaxonomy[termId].children)
                    .map(Number).map((child) => (
                      <option
                        value={child}
                        key={child}
                      >
                        {longformTaxonomy[termId].children[child].name}
                      </option>
                    ))}
                </select>
              </div>
            </Fragment>
          ))}
          <button
            type="button"
            className="clear-filters icon-button"
            aria-label={__('Clear Filters', 'kauffman')}
            onClick={() => {
              const reinitialized = {};
              // eslint-disable-next-line no-return-assign
              filters.forEach((filter) => reinitialized[filter] = 0);
              setFilterValues(reinitialized);
              setSearchString('');
              searchInput.current.value = '';
              setPage(1);
            }}
          >
            <span className="mobile-only">
              {__('Clear Filters', 'kauffman')}
            </span>
            <img src={IconReset} alt="" />
          </button>
        </div>
      </div>
      <div
        id="longform-list-status"
        className="longform-status"
        role="status"
        aria-live="polite"
      >
        {0 === displayedItems.length
          ? __('No results found.', 'kauffman')
          : (
            sprintf( // eslint-disable function-paren-newline
              /* translators: %d: number of results */
              _n(
                'Showing %d item',
                'Showing %d items',
                displayedItems.length,
                'kauffman'
              ),
              displayedItems.length,
            ))}
      </div>
      <div className="items-wrapper">
        <div className="items">
          {displayedItems
            .slice((page - 1) * PER_PAGE, page * PER_PAGE)
            .map((item) => (
              <section
                key={item.id}
                className="item"
              >
                <h3
                  className="item-header"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(item.title.rendered),
                  }}
                />
                <div className="item-body-wrapper">
                  <div
                    className="item-body"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(item.content.rendered),
                    }}
                  />
                  <ul className="item-terms">
                    {Object
                      .entries(prepareTermsForDisplay(
                        item['longform-categorizations'],
                        longformTaxonomy
                      ))
                      .map(([key, value]) => (
                        <li
                          className="item-term"
                          key={key}
                        >
                          <div className="item-term-label">
                            {DOMPurify.sanitize(key)}
                          </div>
                          <div className="item-term-value">
                            {DOMPurify.sanitize(value)}
                          </div>
                        </li>
                      ))}
                  </ul>
                </div>
              </section>
            ))}
        </div>
      </div>

      <div className="longform-pagination">
        {1 < page ?
          <button
            type="button"
            className="longform-page-previous"
            onClick={() => {
              searchInput.current.scrollIntoView();
              setPage(page - 1);
            }}
          >
            {__('Previous Page', 'kauffman')}
          </button>
          : null}

        <span className="longform-page-count">
          {0 < totalPages
            ? `Page ${page} of ${totalPages}`
            : null}
        </span>

        {page < totalPages ?
          <button
            type="button"
            className="longform-page-next"
            onClick={() => {
              searchInput.current.scrollIntoView();
              setPage(page + 1);
            }}
          >
            {__('Next Page', 'kauffman')}
          </button>
          : null}

      </div>
    </div>
  );
};

LongformList.propTypes = {
  parent: PropTypes.string.isRequired,
  postType: PropTypes.string.isRequired,
  excludedFilters: PropTypes.arrayOf(PropTypes.number).isRequired,
  hideTextSearch: PropTypes.bool.isRequired,
};

const appElement = document.getElementById('longform-list-app');

if (appElement) {
  const {
    excludedFilters,
    parentid: parent,
    posttype: postType,
  } = appElement.dataset;

  const hideTextSearch = 'hideTextSearch' in appElement.dataset;

  ReactDOM.render(
    <LongformList
      parent={parent}
      postType={postType}
      excludedFilters={excludedFilters.split(',').map(Number)}
      hideTextSearch={hideTextSearch}
    />,
    appElement
  );
}
